/**
 * Profile domain entity representing a job profile
 *
 * Encapsulates profile information used across the Personnel domain.
 * Immutable by design to ensure data integrity.
 *
 * @module domains/personnel/domain/entities/Profile
 * @example
 * const profile = createProfileFromData({ id: 'profile-1', name: 'Manager' });
 */
import type { ProfileId } from '@personnel/domain/types';

/**
 * Profile entity representing a job profile.
 *
 * Immutable — only created/reconstructed via the factory function.
 */
export type Profile = Readonly<{
  id: ProfileId;
  name: string;
}>;

/**
 * Reconstructs a Profile entity from persisted/raw data.
 *
 * @param params - Raw primitives (typically from the API mapper)
 * @returns Frozen Profile entity
 * @throws Error if id or name is empty
 */
export const createProfileFromData = (params: { id: string; name: string }): Profile => {
  if (!params.id.trim()) {
    throw new Error('Profile id es obligatorio');
  }
  if (!params.name.trim()) {
    throw new Error('Profile name es obligatorio');
  }
  return Object.freeze({
    id: params.id as ProfileId,
    name: params.name,
  });
};
